import { useCallback, useEffect, useState } from 'react'
import { Button } from '../../../design_system/components/Button/Button.jsx'
import { TabGroup } from '../../../design_system/components/Tabs/TabGroup.jsx'
import { TabItem } from '../../../design_system/components/Tabs/TabItem.jsx'
import ActiveTeamMembers from './ActiveTeamMembers.js'
import TeamMembershipRequests from './TeamMembershipRequests.js'
import { Popup } from '../../../design_system/components/Popup/Popup.jsx'
import { InputBox } from '../../../design_system/components/Input Fields/InputBox.jsx'
import { SelectBox } from '../../../design_system/components/Input Fields/SelectBox.jsx'
import { apiAgent } from "../../../axiosConfig";
import { useSelector } from "react-redux";
import { useToast } from '../../../design_system/components/Toast/ToastContext.jsx'
import ConditionalRender from '../../../ConditionalRender.js'
import { Badge } from '../../../design_system/components/Input Fields/Badge.jsx'
import { FileUploader } from '../../../design_system/components/FileUploader/FileUploader.jsx'
import { fetchAccessRoles, fetchAssignableRoles } from './access/api.js'
import { canUseMemberManagementAction, delegatedMemberRoleOptions, isCustomerTeamAdmin, usesPolicyAuthorization } from '../../../utils/access.js'

const all_member_types = [{ label: "Admin", value: "admin" }, { label: "Regular", value: "regular" }, { label: "Passive", value: "passive" }];

export const decodeInviteRole = (value) => value?.startsWith("custom:")
  ? { team_role: "custom", access_role_id: value.slice("custom:".length) }
  : { team_role: value, access_role_id: null };
const ManageTeams = ({ setActiveSettingTab }) => {
  const [activeTab, setActiveTab] = useState('Active')
  const [popupsList, setPopupsList] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const current_team = useSelector(state => state.current_team.team);
  const current_user = useSelector(state => state.current_user.user);
  const customerTeamAdmin = isCustomerTeamAdmin({ team: current_team });
  const canInviteMembers = canUseMemberManagementAction({ permissionKey: "members.invite", user: current_user, team: current_team });
  const canApproveMembers = canUseMemberManagementAction({ permissionKey: "members.approve", user: current_user, team: current_team });
  const canRemoveMembers = canUseMemberManagementAction({ permissionKey: "members.remove", user: current_user, team: current_team });
  const canViewActiveMembers = canInviteMembers || canRemoveMembers;
  const canViewJoinRequests = !current_team.is_sso_enabled && canApproveMembers;
  const visibleViews = [
    ...(canViewActiveMembers ? ["Active"] : []),
    ...(canViewJoinRequests ? ["Join Requests"] : []),
  ];
  const [data, setData] = useState(false);
  const [accessData, setAccessData] = useState({
    permission_catalogue: [],
    system_roles: [],
    custom_roles: []
  });
  const toast = useToast();
  const [, setActiveMemberCount] = useState(0);
  const [joinRequestCount, setJoinRequestCount] = useState(0);
  const [errors, setErrors] = useState({});
  const [importFile, setImportFile] = useState({ file: null, name: null, uploadedUrl: '', uploaded: false });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [inviteType, setInviteType] = useState("by_email");
  const [inviteForm, setInviteForm] = useState({
    name: "",
    email: "",
    position: "",
    teamRole: ""
  })
  let { email, name, position, teamRole } = inviteForm

  const setInviteState = useCallback((payload) => setInviteForm(st => ({ ...st, ...payload })), [])


  useEffect(() => {
    setActiveSettingTab('manage_teams')
    let paramsString = window.location.search;
    let searchParams = new URLSearchParams(paramsString);
    if (searchParams.get('invite') === 'true' && canInviteMembers) {
      setPopupsList({ invitePopup: true });
    }
    if (searchParams.get('to') === 'join_request' && canApproveMembers) {
      setActiveTab('Join Requests')
    }
  }, [setActiveSettingTab, canInviteMembers, canApproveMembers])

  useEffect(() => {
    if (!usesPolicyAuthorization(current_team)) return;

    const request = customerTeamAdmin
      ? fetchAccessRoles()
      : (canInviteMembers || canRemoveMembers)
        ? fetchAssignableRoles()
        : null;
    if (!request) return;

    request
      .then(result => setAccessData(previous => ({ ...previous, ...result })))
      .catch(() => toast.error("Unable to load custom roles"));
  }, [current_team.id, current_team.is_sso_enabled, current_team.policy_access_enabled, customerTeamAdmin, canInviteMembers, canRemoveMembers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!visibleViews.includes(activeTab) && visibleViews.length > 0) {
      setActiveTab(visibleViews[0]);
    }
  }, [activeTab, canViewActiveMembers, canViewJoinRequests]); // eslint-disable-line react-hooks/exhaustive-deps

  const inviteEmail = () => {
    let errors = {};
    const selectedRole = decodeInviteRole(teamRole);
    if (!teamRole) errors["teamRole"] = "Please select member type";
    if (!email && teamRole && teamRole !== "passive") {
      errors["email"] = "Email can't be blank";
    }
    if (!name) {
      errors["name"] = "Name can't be blank";
    }
    if (Object.keys(errors).length > 0) {
      setErrors(errors)
      return;
    }
    setIsLoading(true)
    setData(true)
    let url = `/teams/${current_team.id}/add_team_user`;
    let data_params = {
      email: teamRole === "passive" ? "" : email.toLowerCase(),
      name: name,
      position: position,
      team_role: selectedRole.team_role,
      access_role_id: selectedRole.access_role_id
    }
    apiAgent({
      method: 'post',
      url: url,
      data: data_params
    }).then(res => {
      setIsLoading(false);
      if (teamRole === "passive")
        toast.success("User added to team");
      else if (!current_team.is_sso_enabled)
        toast.success("User invited and added to team");
      else if (current_team.is_sso_enabled && !current_team.is_jit_enabled && !current_team.is_scim_enabled)
        toast.success("User successfully added");
      setData(false)
      closePopup();
    }).catch(err => {
      setIsLoading(false)
      if (teamRole === "passive")
        toast.error(err?.response?.data?.error_message || err?.response?.data?.server_message || err?.response?.data?.error || "Unable to add user");
      errors["email"] = err?.response?.data?.error_message;
      errors["button"] = err?.response?.data?.server_message;
      setErrors(errors)
    })
  }

  const resetImportState = () => {
    setImportFile({ file: null, name: null, uploadedUrl: '', uploaded: false });
    setIsUploading(false);
    setUploadProgress(0);
  }

  const closePopup = () => {
    setPopupsList({});
    setInviteState({ name: "", email: "", position: "", teamRole: "" })
    setErrors({})
    setInviteType("by_email")
    resetImportState();
  }

  const submitImport = () => {
    if (!importFile.uploadedUrl) return;
    setIsLoading(true);
    apiAgent({
      method: 'post',
      url: '/team/bulk_invite_team_members',
      data: { file_url: importFile.uploadedUrl }
    }).then(res => {
      setIsLoading(false);
      toast.success("Users will be added to the team shortly");
      closePopup();
    }).catch(err => {
      setIsLoading(false);
      toast.error(err?.response?.data?.error_message || "Import failed");
    });
  }

  function getTeamMessage() {
    if (!current_team.is_sso_enabled) {
      return TEAM_MESSAGES.normal;
    }
    if (current_team.is_scim_enabled) {
      return TEAM_MESSAGES.sso_scim;
    }
    if (current_team.is_jit_enabled) {
      return TEAM_MESSAGES.sso_jit;
    }
    return TEAM_MESSAGES.sso_only;
  }

  const TEAM_MESSAGES = {
    normal: {
      header: null,
      subtext: "Invite new members, update roles, remove users, and handle incoming join requests for your team.",
    },
    sso_scim: {
      header: "User and Role Management is handled via your Identity Provider",
      subtext:
        "This team is managed through Single Sign-On (SSO) and SCIM provisioning. To add or remove users, or to update roles, please make the changes in your identity provider. Changes will automatically sync to this platform.",
    },
    sso_jit: {
      header: "User creation is handled via your Identity Provider",
      subtext:
        "This team is managed through Single Sign-On (SSO) with Just-in-Time (JIT) provisioning. New users are created automatically when they log in through your IdP and are assigned the default role. Roles and removals must be managed inside this platform.",
    },
    sso_only: {
      header: "User Management requires both your Identity Provider and this platform",
      subtext:
        "This team is managed through Single Sign-On (SSO). Users must first be added to this platform by an admin before they can log in with your IdP. Roles and removals must be managed inside this platform.",
    },
  };


  const inviteRoleOptions = customerTeamAdmin
    ? [
        ...all_member_types,
        ...accessData.custom_roles.map(role => ({
          label: role.name,
          value: "custom:" + role.id
        }))
      ]
    : delegatedMemberRoleOptions({ team: current_team, customRoles: accessData.custom_roles });

  const renderByEmail = () => {
    return (
      <div className='mt-l mb-l flex flex-col gap-l'>
        <InputBox label="Name" showLabel error={errors["name"]}
          value={name} handleOnChange={(e) => { setInviteState({ name: e.target.value }); setErrors({}) }} placeholder="John Doe" />
        <SelectBox label="Role" showLabel customStyle={{ width: "192px"}} error={errors["teamRole"]} variant="ghost"
          triggerStyle={{ border: '1px solid var(--color-border-subtle)' }}
          placeholder="Select Role"
          value={teamRole}
          options={inviteRoleOptions}
          handleOnChange={(val) => { setInviteState({ teamRole: val, ...(val === "passive" ? { email: "" } : {}) }); setErrors({}); }}
          align="left"
          Size="Large"
        />
        <ConditionalRender if={!!teamRole && teamRole !== "passive"}>
          <InputBox label="Email" showLabel error={errors["email"]} showHelperText={!!errors["email"]} helperText={errors["email"]}
            value={email} handleOnChange={(e) => { setInviteState({ email: e.target.value }); setErrors({}) }} placeholder="johndoe@domain.com" />
        </ConditionalRender>
      </div>
    )
  }

  const renderByImport = () => {
    return (
      <>
        <p className="body-s-semibold color-text-primary">Follow the steps to import team members:</p>
        <ol className="body-s-semibold pl-l mt-s color-text-secondary">
          <li>
            Download this sample file - <a href="https://karmabox-temp.s3.amazonaws.com/prod/prod_sample_team_members_import_file.csv" className="link-default">Click Here</a>
          </li>
          <li>
            Fill up the details
          </li>
          <li>
            Save as CSV & Upload
          </li>
        </ol>
        <div className="my-l">
          <FileUploader
            currentFile={importFile}
            setCurrentFile={setImportFile}
            fileUploading={isUploading}
            onUploadBegin={() => setIsUploading(true)}
            onUploadSuccess={(uploadedFile) => {
              setImportFile(uploadedFile);
              setIsUploading(false);
            }}
            itemStyle={{ width: "100%" }}
            dropzoneStyle={{ minHeight: 100 }}
            promptStyle={{ gap: 'var(--space-2xs)' }}
            validFileTypes={['csv']}
            uploadProgress={uploadProgress}
            setUploadProgress={setUploadProgress}
          />
        </div>
      </>
    )
  }

  const invitePopup = () => {
    return (
      <Popup name="Invite members" handleOnClosePopup={() => closePopup()} maxWidth={480}
        primaryAction={inviteType === 'by_email'
          ? { label: teamRole === "passive" ? "Add" : "Invite", onClick: inviteEmail, isLoading }
          : { label: "Invite", onClick: submitImport, isLoading, disabled: !importFile.uploaded }}
        secondaryAction={{ label: "Cancel", onClick: closePopup }}>
        {customerTeamAdmin && (
          <TabGroup variant="line" fullWidth customClass="px-4xl mb-l pt-m">
            <TabItem label="Invite by email" isActive={inviteType === 'by_email'} onClick={() => { setInviteType('by_email'); resetImportState(); setErrors({}); }} />
            <TabItem label="Import team members" isActive={inviteType === 'by_import'} onClick={() => { setInviteType('by_import'); setInviteState({ name: "", email: "", position: "", teamRole: "" }); setErrors({}); }} />
          </TabGroup>
        )}
        {inviteType === "by_email" ? (
          <>
            {renderByEmail()}
            <ConditionalRender if={teamRole !== "passive"}>
              <div style={{ borderTop: '1px solid var(--color-border-subtle)' }} className="pt-l mt-l mb-l">
                <div className="body-m-semibold color-text-primary">Didn't receive the invite email?</div>
                <div className="body-s mt-xs color-text-secondary">
                  You can copy the invite link from the user's action menu to help them access their account.
                </div>
              </div>
            </ConditionalRender>
          </>
        ) : renderByImport()}
      </Popup>)
  }

  const addUserPopup = () => {
    return (
      <Popup name="Add Team Member" handleOnClosePopup={() => closePopup()} maxWidth={480}
        primaryAction={{ label: "Add", onClick: inviteEmail, isLoading }}
        secondaryAction={{ label: "Cancel", onClick: closePopup }}>
        {renderByEmail()}
      </Popup>
    )
  }

  const renderActiveTabContent = () => {
    return (
      <>
        {canViewActiveMembers && (
          <div style={{ display: activeTab === 'Active' ? undefined : 'none' }}>
            <ActiveTeamMembers inviteEmail={data} onCountChange={setActiveMemberCount} accessData={accessData} />
          </div>
        )}
        {canViewJoinRequests && (
          <div style={{ display: activeTab === 'Join Requests' ? undefined : 'none' }}>
            <TeamMembershipRequests onCountChange={setJoinRequestCount} />
          </div>
        )}
      </>
    )
  }

  const message = getTeamMessage();

  return (
    <div className="settings-panel">

      {/* ── Header ── */}
      <div className="settings-panel__header">
        <div className="settings-panel__title body-l-bold">People</div>
        {message.header && (
          <div className="mb-m">
            <Badge type="Warning" label={message.header} ShowLeadingIcon LeadingIcon="info" fontClass="body-s-semibold" />
          </div>
        )}
        <div className="settings-panel__description body-s-semibold">
          {message.subtext}
        </div>
      </div>


      {/* ── Members Section ── */}
      <div className="settings-panel__section">
        <ConditionalRender if={!current_team.is_sso_enabled || (current_team.is_sso_enabled && !current_team.is_scim_enabled && !current_team.is_jit_enabled)}>
          <div className="settings-panel__section-header mb-l">
            {current_team.is_sso_enabled ? (
              <TabGroup variant="line" gap="24" style={{ padding: '0' }}>
                <TabItem variant="line" label={'Active'} isActive={activeTab === 'Active'} onClick={() => setActiveTab('Active')} />
              </TabGroup>
            ) : visibleViews.length > 1 ? (
              <TabGroup variant="line" gap="24" style={{ padding: '0' }}>
                <TabItem variant="line" label={'Active'} isActive={activeTab === 'Active'} onClick={() => setActiveTab('Active')} />
                <TabItem variant="line" label={`Join Requests${joinRequestCount > 0 ? ` (${joinRequestCount})` : ''}`} isActive={activeTab === 'Join Requests'} onClick={() => setActiveTab('Join Requests')} />
              </TabGroup>
            ) : <div />}
            <div className="flex gap-s">
              <ConditionalRender if={canInviteMembers && current_team.is_sso_enabled && !current_team.is_scim_enabled && !current_team.is_jit_enabled}>
                <Button Size="Small" onClick={() => setPopupsList({ addUserPopup: true })}>Add</Button>
              </ConditionalRender>
              <ConditionalRender if={canInviteMembers && !current_team.is_sso_enabled && activeTab === 'Active'}>
                <Button Size="Small" onClick={() => setPopupsList({ invitePopup: true })}>Invite</Button>
              </ConditionalRender>
            </div>
          </div>
        </ConditionalRender>

        {renderActiveTabContent()}
      </div>

      {/* ── Popups ── */}
      {popupsList.invitePopup && invitePopup()}
      {popupsList.addUserPopup && addUserPopup()}

    </div>
  )
}

export default ManageTeams
